import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { IconType } from 'react-icons';
import {
  FaBullhorn,
  FaCalendarAlt,
  FaClipboardList,
  FaLayerGroup,
  FaReceipt,
  FaRegFileAlt,
  FaTachometerAlt,
  FaUserPlus
} from '../../../utils/iconUtils';
 
type Tab = {
  id: string;
  label: string;
  icon: IconType;
  path: string;
  description?: string;
  end?: boolean;
};

const ProgramLayout: React.FC = () => {
  const tabGroups: Array<{ label: string; tabs: Tab[] }> = [
    {
      label: 'Overview',
      tabs: [
        { id: 'dashboard', label: 'Dashboard', icon: FaTachometerAlt, path: 'dashboard', description: 'Performance snapshot' },
        { id: 'program-management', label: 'Programs', icon: FaBullhorn, path: '.', description: 'Create and manage', end: true }
      ]
    },
    {
      label: 'Manage',
      tabs: [
        { id: 'sessions', label: 'Sessions', icon: FaCalendarAlt, path: 'sessions', description: 'Program schedule' },
        { id: 'registrations', label: 'Registrations', icon: FaClipboardList, path: 'registrations', description: 'Attendee records', end: true },
        { id: 'add-member', label: 'Add Member', icon: FaUserPlus, path: 'registrations/add', description: 'Manual entry', end: true },
        { id: 'events', label: 'Events', icon: FaReceipt, path: 'events', description: 'Event receipts' }
      ]
    },
    {
      label: 'Output',
      tabs: [
        { id: 'publish', label: 'Publish', icon: FaLayerGroup, path: 'publish', description: 'Go public' },
        { id: 'report', label: 'Report', icon: FaRegFileAlt, path: 'report', description: 'Print and export' }
      ]
    }
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 shadow-soft overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-b border-gray-200">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              Programs
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Manage programs, sessions, registrations, event receipts, publishing, and reports.
            </p>
          </div>

          <div className="hidden lg:flex items-center gap-2 text-xs text-gray-500">
            <span className="h-2 w-2 rounded-full bg-green-500"></span>
            Organized workspace
          </div>
        </div>

        <div className="p-3">
          <div className="grid grid-cols-1 xl:grid-cols-[0.85fr_1.35fr_0.8fr] gap-3">
            {tabGroups.map((group) => (
              <div key={group.label} className="rounded-lg border border-gray-200 bg-white p-2">
                <div className="px-2 pb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                  {group.label}
                </div>
                <div className="flex flex-wrap gap-2">
                  {group.tabs.map((tab) => (
                    <NavLink
                      key={tab.id}
                      to={tab.path}
                      end={tab.end ?? false}
                      className={({ isActive }) =>
                        `inline-flex min-h-[3rem] flex-1 basis-[9.5rem] items-center gap-2 rounded-md px-3 py-2 text-sm transition-all ${
                          isActive
                            ? 'bg-primary-600 text-white shadow'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`
                      }
                    >
                      <tab.icon className="h-4 w-4 flex-shrink-0" />
                      <div className="min-w-0 text-left">
                        <span className="block truncate font-semibold text-sm leading-tight">{tab.label}</span>
                        <span className="block truncate text-xs opacity-75 leading-tight">{tab.description}</span>
                      </div>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="min-h-[420px]">
        <Outlet />
      </div>
      
    </div>
  );
};

export default ProgramLayout;
